import { Box, Drawer, Hidden, List, ListSubheader } from '@material-ui/core'
// material
import { experimentalStyled as styled } from '@material-ui/core/styles'
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { Link as RouterLink, matchPath, useLocation } from 'react-router-dom'
import useApi from 'src/hooks/useApi'
import Scrollbar from '../../components/Scrollbar'
//
import MenuLinks from './SidebarConfig'
import SidebarItem from './SidebarItem'
import _ from 'lodash'
import FlashOnIcon from '@material-ui/icons/FlashOn'
import { hasMissingAssistantTariff, isAssistantEnabled, isScoutChatEnabled } from 'src/utils/assistantAccess'
import { canAccessErp, getErpFeatures, hasErpRole, isStandaloneErp } from 'src/utils/erpAccess'

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: DRAWER_WIDTH,
  },
}))

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  margin: theme.spacing(1, 2.5, 5),
  borderRadius: theme.shape.borderRadiusSm,
  backgroundColor: theme.palette.grey[500_12],
}))

// ----------------------------------------------------------------------

function reduceChild({ array, item, pathname, level, site }) {
  const key = item.href + level
  if (item.items) {
    const match = matchPath(pathname, {
      path: item.href,
      exact: false,
    })
    const forceOpen = item.title === "ERP" && isStandaloneErp(site)

    array = [
      ...array,
      <SidebarItem
        key={key}
        level={level}
        icon={item.icon}
        info={item.info}
        href={item.href}
        title={item.title}
        open={forceOpen || Boolean(match)}
      >
        {renderSidebarItems({
          pathname,
          level: level + 1,
          items: item.items,
          site,
        })}
      </SidebarItem>,
    ]
  } else {
    array = [
      ...array,
      <SidebarItem
        key={key}
        level={level}
        href={item.href}
        icon={item.icon}
        info={item.info}
        title={item.title}
      />,
    ]
  }
  return array
}

function renderSidebarItems({ items, pathname, level = 0, site }, isAccount, isAdmin, user, isReporting) {
  let authItems = [...items]

  if (isReporting) {
    _.remove(authItems, (i) => i.title == "Templates")
  }

  if(!isAccount && !isAdmin){
    _.remove(authItems, (i) => i.title == "Account")
    _.remove(authItems, (i) => i.title == "Integrations")
    _.remove(authItems, (i) => i.title == "Network")
    _.remove(authItems, (i) => i.title == "Configuration")
    _.remove(authItems, (i) => i.title == "Scout")
    _.remove(authItems, (i) => i.title == "Scout Chat")
    
  }

  if(!isAdmin){
    _.remove(authItems, (i) => i.title == "Developer")
    _.remove(authItems, (i) => i.title == "Frigid Site Overview")    
    _.remove(authItems, (i) => i.title == "Maintenance")    
  }

  if(!site || !site.config?.admin?.network){
    _.remove(authItems, (i) => i.title == "Network")
  }

  if (!isAssistantEnabled(site)) {
    _.remove(authItems, (i) => i.title == "Scout")
    _.remove(authItems, (i) => i.title == "Plant Insights")
  }

  if (!isScoutChatEnabled(site, user)) {
    _.remove(authItems, (i) => i.title == "Scout Chat")
  }

  // ERP: site must have it enabled AND user must have access.
  // Sub-items filtered by the site's tier feature set and the user's ERP roles.
  if (!canAccessErp(site, user)) {
    _.remove(authItems, (i) => i.title == "ERP")
  } else {
    const features = getErpFeatures(site)
    authItems = authItems.map((item) => {
      if (item.title !== "ERP" || !item.items) return item
      return {
        ...item,
        items: item.items.filter((sub) => {
          const featureOk = !sub.feature || features.includes(sub.feature)
          const roleOk = !sub.roles || hasErpRole(user, sub.roles)
          return featureOk && roleOk
        })
      }
    })
  }

  // Standalone ERP sites (no Frigid hardware): strip hardware-centric nav.
  // Role-gated items (Developer/Admin/Maintenance) are already filtered above.
  if (isStandaloneErp(site)) {
    _.remove(authItems, (i) => i.title == "Dashboard")
    _.remove(authItems, (i) => i.title == "Site Map")
    _.remove(authItems, (i) => i.title == "Energy")
    _.remove(authItems, (i) => i.title == "Plant Insights")
    _.remove(authItems, (i) => i.title == "Batches")
    _.remove(authItems, (i) => i.title == "Templates")
    _.remove(authItems, (i) => i.title == "Logs")
    _.remove(authItems, (i) => i.title == "Integrations")
    _.remove(authItems, (i) => i.title == "Network")
    _.remove(authItems, (i) => i.title == "Configuration")
    _.remove(authItems, (i) => i.title == "Data Export")
    _.remove(authItems, (i) => i.title == "Maintenance")
    _.remove(authItems, (i) => i.title == "Scout Chat")
    _.remove(authItems, (i) => i.title == "User guide")
    _.remove(authItems, (i) => i.title == "Changelog")
  }

  if (hasMissingAssistantTariff(site)) {
    authItems = authItems.map((item) => (
      item.title === "Configuration"
        ? {
            ...item,
            info: (
              <FlashOnIcon
                style={{
                  width: 18,
                  height: 18,
                  color: "#ff8a00",
                  marginLeft: 8,
                  flexShrink: 0,
                }}
              />
            ),
          }
        : item
    ))
  }

  return (
    <List disablePadding>
      {authItems.reduce(
        (array, item) => reduceChild({ array, item, pathname, level, site }),
        []
      )}
    </List>
  )
}

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
  offsetTop: PropTypes.number,
}

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar, offsetTop = 0 }) {
  const { pathname } = useLocation()

  const api = useApi()

  //console.log('user', api.user);

  useEffect(() => {
    if (isOpenSidebar && onCloseSidebar) {
      onCloseSidebar()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  let webAppVersion
  try {
    webAppVersion = require('../../../package.json').version
  } catch (e) {
    webAppVersion = 'unknown'
  };
  const renderContent = (
      <Scrollbar>
        <Box
            sx={{px: 2.5, py: 3, pb: 0}}
            style={{display: 'flex', justifyContent: 'center'}}
        >
          <RouterLink to="/">
            <Box
                component="img"
                alt="welcome"
                src="/static/brand/frigid.png"
            />
          </RouterLink>
        </Box>

        {MenuLinks.map((list) => (
            <List
                disablePadding
                key={list.subheader}
                subheader={
                  <ListSubheader
                      disableSticky
                      disableGutters
                      sx={{
                        mt: 3,
                        mb: 2,
                        pl: 5,
                        color: 'text.primary',
                        typography: 'overline',
                      }}
                  >
                    {list.subheader}
                  </ListSubheader>
                }
            >
              {renderSidebarItems({
                items: list.items,
                pathname,
                site: api.site,
              }, api.user && api.user?.role && (api.user?.role == "account"), api.user && api.user?.role && (api.user?.role == "admin"), api.user, api.user?.role === "reporting")}
            </List>
        ))}

        <div style={{fontSize: 12, paddingLeft: 35, paddingRight: 10, paddingTop: 20}}>Got a feature you'd like to see
          in Frigid.Cloud? <a href='https://frigid.cloud/feature-request' target='_blank'>Request it here</a>.
        </div>
        <div style={{fontSize: 12, paddingLeft: 35, paddingRight: 10, paddingTop: 10, color: "rgba(68,81,103,0.66)"}}>v {webAppVersion}</div>

      </Scrollbar>
  )

  return (
      <RootStyle>
        <Hidden lgUp>
          <Drawer
              open={isOpenSidebar}
              onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH, top: offsetTop },
          }}
        >
          {renderContent}
        </Drawer>
      </Hidden>
      <Hidden lgDown>
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: { width: DRAWER_WIDTH, bgcolor: 'background.default', top: offsetTop },
          }}
        >
          {renderContent}
        </Drawer>
      </Hidden>
    </RootStyle>
  )
}
